import type { AreaId, PersonId, SubAreaId } from '@shared/brand';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { RuntimeConfig } from '@domain/entities/RuntimeConfig';
import { featureLiveInArea } from '@domain/entities/AreaCatalog';
import { can, canAny } from '@domain/policies/RbacPolicy';
import type { FeatureKey, Permission, Role } from '@domain/vocabulary';

/**
 * The session a use case acts within: who is asking, and the configuration in
 * force right now.
 *
 * Passed explicitly rather than read from a global. A use case that reaches for
 * ambient state cannot be tested against two different roles in the same suite,
 * and — more importantly — it becomes unclear whose authority an action is being
 * taken under, which is precisely the thing an audit log has to answer.
 */
export interface Session {
  readonly personId: PersonId;
  readonly displayName: string;
  readonly email: string;
  readonly role: Role;
  readonly areaId: AreaId;
  readonly subAreaId: SubAreaId | null;
  /**
   * De dónde sale el periodo de vacaciones de quien mira.
   *
   * Va en la sesión y no se lee por separado porque las cuatro pantallas que
   * enseñan «tu saldo» —el shell, Inicio, Perfil y el asistente— lo necesitan
   * para saber qué documento pedir: el saldo se guarda por periodo de
   * aniversario, no por año calendario.
   */
  readonly hireDate: CalendarDate | null;
  readonly config: RuntimeConfig;
}

/**
 * Whether the session's role holds a permission under the live matrix.
 *
 * This gates what the UI offers. It is not the security boundary — the Firestore
 * rules and the privileged callables are, and they authorise off custom claims
 * the client cannot edit. Keeping the distinction sharp is what makes it safe for
 * the matrix to be runtime-editable at all.
 */
export const sessionCan = (session: Session, permission: Permission): boolean =>
  can(session.config.rolePermissions, session.role, permission);

export const sessionCanAny = (session: Session, permissions: readonly Permission[]): boolean =>
  canAny(session.config.rolePermissions, session.role, permissions);

/**
 * Whether a module is switched on **for this session's area**.
 *
 * La regla entera vive en `featureLiveInArea`, en el dominio, y aquí sólo se le
 * pasa el área de quien mira. Es a propósito: la misma pregunta se hace desde el
 * cliente, desde los casos de uso y desde el servidor, y tres implementaciones
 * son tres formas de que una discrepe — que es lo que este repositorio ya pagó
 * con `Role` escrito a mano en tres sitios.
 */
export const featureOn = (session: Session, feature: FeatureKey): boolean =>
  featureLiveInArea(session.config.features, session.config.areaCatalogs[session.areaId], feature);

export type AccessDenial =
  | { readonly kind: 'missing-permission'; readonly permission: Permission; readonly role: Role }
  | { readonly kind: 'feature-off'; readonly feature: FeatureKey }
  | { readonly kind: 'not-active' }
  | { readonly kind: 'out-of-scope'; readonly detail: string };

/**
 * The guard every use case opens with. Returns the specific denial rather than a
 * boolean so the UI can say "Tu perfil (Colaborador) no tiene acceso a esa sección"
 * instead of a generic refusal — the handoff asks for exactly that wording.
 */
export const requireAccess = (
  session: Session,
  requirements: {
    readonly permission?: Permission;
    readonly anyPermission?: readonly Permission[];
    readonly feature?: FeatureKey;
  },
): AccessDenial | null => {
  if (requirements.feature !== undefined && !featureOn(session, requirements.feature)) {
    return { kind: 'feature-off', feature: requirements.feature };
  }
  if (requirements.permission !== undefined && !sessionCan(session, requirements.permission)) {
    return { kind: 'missing-permission', permission: requirements.permission, role: session.role };
  }
  if (
    requirements.anyPermission !== undefined &&
    requirements.anyPermission.length > 0 &&
    !sessionCanAny(session, requirements.anyPermission)
  ) {
    const first = requirements.anyPermission[0];
    if (first !== undefined) {
      return { kind: 'missing-permission', permission: first, role: session.role };
    }
  }
  return null;
};

/**
 * Whether the session may act on data belonging to `areaId`. A superuser is
 * company-wide; everyone else is confined to their own area, mirroring the
 * `inScope()` helper in the security rules so the client never offers an action
 * the server will refuse.
 */
export const inScope = (session: Session, areaId: AreaId): boolean =>
  session.role === 'super' || session.areaId === areaId;
